import React, { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import NotificationService, { SystemNotification } from '../../lib/services/notificationService';
import { Bell, Trash2, CheckSquare } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'react-hot-toast';
import { Spinner } from '../../components/Spinner';
import SEO from '../../components/SEO';
import { useTheme } from '../../contexts/ThemeContext';

const notificationService = new NotificationService();

const NotificationsPage: React.FC = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [notifications, setNotifications] = useState<SystemNotification[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadNotifications();
  }, [user?.id]);

  const loadNotifications = async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      const allNotifications = await notificationService.getUserNotifications(user.id);
      console.log('[NotificationsPage] Notifications loaded:', allNotifications);
      setNotifications(allNotifications);
    } catch (error) {
      console.error('Erro ao carregar notificações:', error);
      toast.error('Não foi possível carregar as notificações');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId: string) => {
    if (!user?.id) return;
    try {
      await notificationService.markAsRead(notificationId, user.id);
      setNotifications(notifications.map(notification => {
        if (notification.id === notificationId) {
          const currentStatus = notification.user_notification_status;
          if (!currentStatus) {
            console.error('[NotificationsPage] UserNotificationStatus is missing for notification:', notification.id);
            return notification;
          }
          return {
            ...notification,
            user_notification_status: {
              ...currentStatus,
              read: true,
              read_at: new Date().toISOString(),
            }
          };
        }
        return notification;
      }));
      toast.success('Notificação marcada como lida');
    } catch (error) {
      console.error('Erro ao marcar notificação como lida:', error);
      toast.error('Erro ao marcar notificação como lida');
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!user?.id) return;
    try {
      await notificationService.markAllAsRead(user.id);
      setNotifications(notifications.map(notification => {
        const currentStatus = notification.user_notification_status;
        if (!currentStatus) {
          console.error('[NotificationsPage] UserNotificationStatus is missing for notification during MarkAllAsRead:', notification.id);
          return notification;
        }
        return {
          ...notification,
          user_notification_status: {
            ...currentStatus,
            read: true,
            read_at: new Date().toISOString(),
          }
        };
      }));
      toast.success('Todas as notificações foram marcadas como lidas');
    } catch (error) {
      console.error('Erro ao marcar todas notificações como lidas:', error);
      toast.error('Erro ao marcar notificações como lidas');
    }
  };

  const handleDeleteNotification = async (notificationId: string) => {
    try {
      await notificationService.deleteNotification(notificationId);
      setNotifications(notifications.filter(n => n.id !== notificationId));
      toast.success('Notificação excluída com sucesso');
    } catch (error) {
      console.error('Erro ao excluir notificação:', error);
      toast.error('Erro ao excluir notificação');
    }
  };

  return (
    <>
      <SEO 
        title="Notificações"
        description="Suas notificações do sistema"
      />
      
      <div className={`container mx-auto max-w-4xl px-4 py-8 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-900'}`}>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className={`text-2xl font-bold flex items-center gap-2 ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>
              <Bell className="h-6 w-6" />
              Notificações
            </h1>
            <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mt-1`}>
              Gerencie suas notificações do sistema
            </p>
          </div>
          
          {notifications.some(n => !n.user_notification_status?.read) && (
            <button
              onClick={handleMarkAllAsRead}
              className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white 
                          ${theme === 'dark' ? 'bg-indigo-500 hover:bg-indigo-600 focus:ring-indigo-600' : 'bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500'}
                          focus:outline-none focus:ring-2 focus:ring-offset-2 ${theme === 'dark' ? 'dark:ring-offset-gray-800' : ''}`}
            >
              <CheckSquare className="h-4 w-4 mr-2" />
              Marcar todas como lidas
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : notifications.length === 0 ? (
          <div className={`text-center py-12 rounded-lg border ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
            <Bell className={`mx-auto h-12 w-12 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`} />
            <h3 className={`mt-4 text-lg font-medium ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>Nenhuma notificação</h3>
            <p className={`mt-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              Você não tem notificações para visualizar no momento.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={`rounded-lg shadow-sm border p-4 transition-colors ${ 
                  notification.user_notification_status?.read 
                    ? (theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200') 
                    : (theme === 'dark' ? 'bg-indigo-900/30 border-indigo-700' : 'bg-indigo-50 border-indigo-200')
                }`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className={`font-medium ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>{notification.title}</h3>
                    <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mt-1`}>{notification.body}</p>
                    {notification.url && (
                      <a
                        href={notification.url}
                        className={`text-sm mt-2 inline-block ${theme === 'dark' ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-800'}`}
                      >
                        Saiba mais
                      </a>
                    )}
                    <div className={`mt-2 text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                      {formatDistanceToNow(new Date(notification.created_at), { 
                        addSuffix: true,
                        locale: ptBR 
                      })}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 ml-4">
                    {!notification.user_notification_status?.read && (
                      <button
                        onClick={() => handleMarkAsRead(notification.id)}
                        className={`p-1 ${theme === 'dark' ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-800'}`}
                        title="Marcar como lida"
                      >
                        <CheckSquare className="h-5 w-5" />
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteNotification(notification.id)}
                      className={`p-1 ${theme === 'dark' ? 'text-gray-500 hover:text-red-400' : 'text-gray-400 hover:text-red-600'}`}
                      title="Excluir notificação"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default NotificationsPage; 